/**
 * Dimensions, as primitives.
 *
 * The witness lines, the ticks and the numbers that make a drawing measurable rather than
 * merely a picture. It exists as its own module for one reason: PlanView.vue had all of this
 * as Vue computeds, so the screen and any printed drawing would have been two
 * implementations of the same arithmetic, and the first time somebody changed one the
 * drawing would have started lying.
 *
 * Everything here is in MODEL millimetres except `size`, which is paper -- see drawing.js
 * for why. Pure: geometry in, primitives out.
 */
import * as room from './room.js'

/** How far off the thing being measured the dimension line sits, in model mm. */
const OFFSET = 320
/** How far past the dimension line the witness lines run. */
const OVERSHOOT = 90
/** Half the length of the tick through a dimension line. */
const TICK = 70

/**
 * A chain of dimensions along a line of points.
 *
 * Each consecutive pair gets its own dimension: this is a running chain, not a set of
 * overall sizes, because that is what a cabinet maker measures against. Ticks rather than
 * arrowheads, which is the architectural convention and legible at 1:50 where an arrowhead
 * is a smudge.
 *
 * @param {Array<{x,y}>} pts   in order along the line
 * @param {{ n: {x,y}, offset?: number, layer?: string, size?: number, skipUnder?: number }} o
 *   `n` is the direction to push the dimension line, away from the thing measured.
 */
export function chain(pts, o = {}) {
  const prims = []
  if (!pts || pts.length < 2) return prims
  const n = o.n
  const off = o.offset ?? OFFSET
  const layer = o.layer ?? 'dim'
  const size = o.size ?? 2.4
  const skipUnder = o.skipUnder ?? 60

  const out = (p, d) => ({ x: p.x + n.x * d, y: p.y + n.y * d })
  // The witness lines, from the thing measured out past the dimension line.
  for (const p of pts) {
    prims.push({ k: 'line', layer, a: out(p, off - OVERSHOOT * 2), b: out(p, off + OVERSHOOT) })
  }
  // The dimension line itself, and a number in the middle of each span.
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1]
    const len = Math.hypot(b.x - a.x, b.y - a.y)
    if (len < skipUnder) continue
    const A = out(a, off), B = out(b, off)
    prims.push({ k: 'line', layer, a: A, b: B })
    const d = { x: (B.x - A.x) / len, y: (B.y - A.y) / len }
    const perp = { x: -d.y, y: d.x }
    for (const P of [A, B]) {
      prims.push({ k: 'line', layer,
        a: { x: P.x - perp.x * TICK - d.x * TICK, y: P.y - perp.y * TICK - d.y * TICK },
        b: { x: P.x + perp.x * TICK + d.x * TICK, y: P.y + perp.y * TICK + d.y * TICK } })
    }
    prims.push({
      k: 'text', layer, size, align: 'centre',
      at: { x: (A.x + B.x) / 2 + perp.x * 120, y: (A.y + B.y) / 2 + perp.y * 120 },
      // Along the dimension, and never upside down.
      rot: readable(Math.atan2(d.y, d.x) * 180 / Math.PI),
      text: String(Math.round(len)),
    })
  }
  return prims
}

/** Text along a line, turned the right way up: a number read from below is not a number. */
function readable(deg) {
  let a = deg
  while (a > 90) a -= 180
  while (a < -90) a += 180
  return Math.round(a * 10) / 10
}

/**
 * Every wall's length, outside the room.
 *
 * Lifted out of PlanView's `wallsD`, which had it as a label in a pill. Here it is a proper
 * dimension: witness lines, ticks, and the number along the wall rather than across it.
 */
export function wallDims(plan, opts = {}) {
  const prims = []
  for (const w of plan.walls) {
    const n = room.inward(w)
    prims.push(...chain([{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }], {
      n: { x: -n.x, y: -n.y }, offset: opts.offset ?? 520, layer: 'dim',
    }))
  }
  return prims
}

/**
 * The angle at every corner that is not square.
 *
 * A right angle needs no label -- it is the assumption. Anything else is the whole reason
 * the drawing exists, because a splayed corner is what a maker has to cut to.
 */
export function angleDims(plan) {
  const prims = []
  const walls = plan.walls
  for (const w of walls) {
    const before = room.prev(walls, w)
    const deg = Math.round(room.corner(before, w) * 180 / Math.PI)
    if (Math.abs(deg - 90) < 1) continue
    const na = room.inward(before), nb = room.inward(w)
    const bx = na.x + nb.x, by = na.y + nb.y
    const bl = Math.hypot(bx, by) || 1
    prims.push({ k: 'text', layer: 'dim', size: 2.4, align: 'centre',
      at: { x: w.x1 + bx / bl * 420, y: w.y1 + by / bl * 420 }, text: `${deg}°` })
  }
  return prims
}
